import { 
  CContainer, CRow, CCol, CCard, CCardBody, 
  CFormInput, CFormSelect, CButton, CFormLabel,
  CTable, CTableHead, CTableHeaderCell, CTableBody, CTableRow, CTableDataCell
} from '@coreui/react';
import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { CustomBreadcrumbs, LoadingOverlay } from '../../components';
import TableFooter from '../../components/Table/TableFooter';
import styles from './ViewQrTagBatch.module.css';
import tableStyles from '../../components/Table/Table.module.css';

const ViewQrTagBatch = () => {
  const { qrTagBatchId } = useParams();
  const navigate = useNavigate();
  
  const { 
    getQrTagBatch,
    getQrTagsByBatch,
  } = useContext(AppContext);
  
  const [loading, setLoading] = useState(true);
  const [batchData, setBatchData] = useState(null);
  
  // QR Tags con paginación
  const [qrTags, setQrTags] = useState([]);
  const [qrTagsPagination, setQrTagsPagination] = useState(null);
  const [qrTagsPageUrl, setQrTagsPageUrl] = useState(null);

  // Cargar datos del batch + QR tags
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Cargar datos del batch
        const batchRes = await getQrTagBatch(qrTagBatchId);
        setBatchData(batchRes.data || batchRes);
        
        // Cargar QR tags (primera página)
        await fetchQrTags();
        
      } catch (error) {
        console.error('Error loading data:', error);
        alert('Error al cargar los datos. Por favor, intentá de nuevo.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [qrTagBatchId, getQrTagBatch]);

  // Fetch de QR tags con paginación
  const fetchQrTags = async (url = null) => {
    try {
      const params = {};
      
      // Si viene una URL de paginación, extraer los params
      if (url) {
        const urlObj = new URL(url);
        for (const [key, value] of urlObj.searchParams) {
          params[key] = value;
        }
      }
      
      const response = await getQrTagsByBatch(qrTagBatchId, params);
      setQrTags(response.data || []);
      setQrTagsPagination(response.meta || response.links || null);
    } catch (error) {
      console.error('Error fetching QR tags:', error);
    }
  };

  // Handler para paginación
  const handlePageChange = (url) => {
    if (!url) return;
    fetchQrTags(url);
  };

  // Transformar QR tags para la tabla
  const tableData = qrTags.map(tag => ({
    id: tag.id,
    status: tag.pet ? 'Asignado' : 'Sin asignar',
    statusClass: tag.pet ? styles.assigned : styles.unassigned,
    pet_name: tag.pet?.name || '—',
    pet_specie: tag.pet?.specie?.name || '—',
    pet_breed: tag.pet?.breed?.name || '—',
    pet_identification: tag.pet?.pet_identification || '—',
    created_at: tag.created_at ? new Date(tag.created_at).toLocaleDateString('es-AR') : '—',
  }));

  // Si aún está cargando, mostrar loading
  if (loading || !batchData) {
    return (
      <div>
        <LoadingOverlay isVisible={true} message="Cargando datos..." />
      </div>
    );
  }

  return (
    <div>
      <CustomBreadcrumbs 
        parentTitle={'QR Tags'} 
        activeTitle={`Ver Lote #${qrTagBatchId}`} 
      />
      
      <CContainer className="mt-4">
        <CRow className="mb-4">
          <CCol xs={12}>
            <h2 className="title mb-4">Detalles del Lote</h2>
          </CCol>
        </CRow>

        {/* Sección 1: Datos del Lote (Read-only) */}
        <CCard className="mb-4">
          <CCardBody>
            <h4 className="mb-3">Datos del Lote</h4>
            <CRow>
              <CCol xs={12} lg={4}>
                <CFormLabel>Cantidad de QRs</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.qr_tags_amount || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
              
              <CCol xs={12} lg={4}>
                <CFormLabel>Tamaño de Badge</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.badge_size?.name || batchData.badge_size_name || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>

              <CCol xs={12} lg={4}>
                <CFormLabel>Estado</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.qr_tag_batch_status?.name || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        {/* Sección 2: Empresa */}
        <CCard className="mb-4">
          <CCardBody>
            <h4 className="mb-3">Empresa</h4>
            <CRow>
              <CCol xs={12} lg={6}>
                <CFormLabel>Empresa</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.company?.company_name || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
              
              <CCol xs={12} lg={6}>
                <CFormLabel>Nombre de Empresa</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.company_name || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
            </CRow>
            <CRow className="mt-3">
              <CCol xs={12} lg={6}>
                <CFormLabel>Propietario</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.owner_name || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
              
              <CCol xs={12} lg={6}>
                <CFormLabel>Email del Propietario</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.email || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
            </CRow>
            <CRow className="mt-3">
              <CCol xs={12} lg={6}>
                <CFormLabel>Facturación</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.billing_name || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
              
              <CCol xs={12} lg={6}>
                <CFormLabel>CUIT</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.billing_cuit || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        {/* Sección 3: Contacto */}
        <CCard className="mb-4">
          <CCardBody>
            <h4 className="mb-3">Persona de Contacto</h4>
            <CRow>
              <CCol xs={12} lg={6}>
                <CFormLabel>Nombre</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.contact_name || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
              
              <CCol xs={12} lg={6}>
                <CFormLabel>Email</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.contact_email || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
            </CRow>
            <CRow className="mt-3">
              <CCol xs={12} lg={4}>
                <CFormLabel>País</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.contact_phone_country_code || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
              <CCol xs={12} lg={4}>
                <CFormLabel>Área</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.contact_phone_area_code || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
              <CCol xs={12} lg={4}>
                <CFormLabel>Teléfono</CFormLabel>
                <CFormInput 
                  type="text" 
                  value={batchData.contact_phone_number || '—'} 
                  readOnly 
                  plaintext
                />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        {/* Sección 4: QR Tags del Lote */}
        <CCard className="mb-4">
          <CCardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">QR Tags del Lote</h4>
              {batchData.qr_tags_amount > 0 && (
                <CButton 
                  color="success" 
                  size="sm"
                  onClick={() => window.location.href = `/admin/qr-tag-batches/${qrTagBatchId}/download-qr-sheet`}
                >
                  📄 Descargar Hoja de QRs
                </CButton>
              )}
            </div>
            
            {qrTags.length === 0 ? (
              <div className="text-center py-4 text-muted">
                Este lote aún no tiene QR tags generados.
              </div>
            ) : (
              <>
                <div className="col-12">
                <div className={tableStyles.tableCard}>
                <CTable responsive className={tableStyles.styledTable}>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell scope="col">#</CTableHeaderCell>
                      <CTableHeaderCell scope="col">Estado</CTableHeaderCell>
                      <CTableHeaderCell scope="col">Mascota</CTableHeaderCell>
                      <CTableHeaderCell scope="col">Especie</CTableHeaderCell>
                      <CTableHeaderCell scope="col">Raza</CTableHeaderCell>
                      <CTableHeaderCell scope="col">ID Mascota</CTableHeaderCell>
                      <CTableHeaderCell scope="col">Creado</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {tableData.map(tag => (
                      <CTableRow key={tag.id}>
                        <CTableDataCell>{tag.id}</CTableDataCell>
                        <CTableDataCell>
                          <span className={`${styles.badge} ${tag.statusClass}`}>
                            {tag.status}
                          </span>
                        </CTableDataCell>
                        <CTableDataCell className="fw-semibold">
                          {tag.pet_name}
                        </CTableDataCell>
                        <CTableDataCell>{tag.pet_specie}</CTableDataCell>
                        <CTableDataCell>{tag.pet_breed}</CTableDataCell>
                        <CTableDataCell>
                          {tag.pet_identification !== '—' ? (
                            <code className={styles.code}>{tag.pet_identification}</code>
                          ) : '—'}
                        </CTableDataCell>
                        <CTableDataCell>{tag.created_at}</CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
                </div>
                </div>
                
                {/* Paginación */}
                {qrTagsPagination && (
                  <TableFooter 
                    range={qrTagsPagination.links} 
                    setPageUrl={handlePageChange}
                    currentPage={qrTagsPagination.current_page}
                    totalPages={qrTagsPagination.last_page}
                  />
                )}
              </>
            )}
          </CCardBody>
        </CCard>

        {/* Botones de acción */}
        <CRow className="mb-5">
          <CCol xs={12}>
            <div className="d-flex gap-3">
              <CButton 
                color="primary"
                onClick={() => navigate(`/dashboard/qr-tag-batches/edit/${qrTagBatchId}`)}
              >
                ✏️ Editar Lote
              </CButton>
              <CButton 
                color="secondary" 
                onClick={() => navigate('/dashboard/qr-tag-batches')}
              >
                Volver al Listado
              </CButton>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default ViewQrTagBatch;